import { PublicClientApplication, EventType } from '@azure/msal-browser';
import type { EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { msalConfig } from './msalConfig';

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL instance before rendering the app.
 * This handles redirect promise and sets the active account.
 */
export async function initializeMsal(): Promise<void> {
  try {
    // MSAL v3 requires explicit initialization
    await msalInstance.initialize();
    
    // Handle redirect promise
    const response = await msalInstance.handleRedirectPromise();
    
    if (response) {
      // If we have a response from redirect, set the active account
      msalInstance.setActiveAccount(response.account);
      console.log('MSAL: Authentication successful via redirect', response.account?.username);
    } else {
      // If no redirect response, check if we have an account in cache
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        // Set the first account as active
        msalInstance.setActiveAccount(accounts[0]);
        console.log('MSAL: Active account set from cache', accounts[0].username);
      }
    }
  } catch (error) {
    console.error('MSAL initialization error:', error);
    throw error;
  }
}

/**
 * Register event callbacks for MSAL instance.
 * This is useful for handling login success, logout, etc.
 */
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);
    console.log('MSAL: Login success event', account?.username);
  }

  if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
    console.log('MSAL: Token acquired successfully');
  }

  if (event.eventType === EventType.LOGIN_FAILURE) {
    console.error('MSAL: Login failure', event.error);
  }

  if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
    console.error('MSAL: Token acquisition failure', event.error);
  }

  if (event.eventType === EventType.LOGOUT_SUCCESS) {
    console.log('MSAL: Logout success');
  }
});

export default msalInstance;
